import { useState } from "react";
import { FiLoader } from "react-icons/fi";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import { uploadFile } from "../../utils/HelperMethod";

const MAX_IMAGES = 5;

export default function Step3Media({
  defaultValues,
  onNext,
  onBack,
  isSubmitting,
}) {
  const [localImages, setLocalImages] = useState(defaultValues.images || []);

  const syncAndNext = () => {
    const validImages = localImages.filter((img) => {
      if (typeof img === "string") return true;
      return img.url; // uploaded images only
    });

    if (validImages.length < 1) {
      toast.error("At least 1 image is required");
      return;
    }

    onNext({ images: validImages });
  };

  /* ================= IMAGE UPLOAD ================= */
  const onDropImages = async (files) => {
    const incomingFiles = Array.from(files || []);

    if (!incomingFiles.length) return;

    if (localImages.length + incomingFiles.length > MAX_IMAGES) {
      toast.error("You can upload up to 5 images only");
      return;
    }

    const newImages = incomingFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file),
      url: null,
      isUploading: true,
      isExisting: false,
    }));

    setLocalImages((prev) => [...prev, ...newImages]);

    for (const img of newImages) {
      try {
        const compressed = await imageCompression(img.file, {
          maxSizeMB: 0.6,
          maxWidthOrHeight: 1200,
          useWebWorker: true,
        });

        const uploadedUrl = await uploadFile(compressed, "experienceImage");

        if (uploadedUrl) {
          setLocalImages((prev) =>
            prev.map((item) =>
              item.id === img.id
                ? {
                    ...item,
                    url: uploadedUrl.split("?")[0],
                    isUploading: false,
                    isExisting: true,
                    file: null,
                  }
                : item,
            ),
          );
        }
      } catch (err) {
        toast.error("Failed to upload image");
        setLocalImages((prev) =>
          prev.map((item) =>
            item.id === img.id
              ? { ...item, isUploading: false, uploadError: true }
              : item,
          ),
        );
      }
    }
  };

  const removeImage = (index) => {
    setLocalImages((prev) => prev.filter((_, i) => i !== index));
  };

  const isUploading = localImages.some((img) => img.isUploading);

  const getImagePreview = (img) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    return img.preview || img.url || "";
  };

  /* ================= UI ================= */
  return (
    <div
      className="
        bg-white rounded-[12px] border border-[#D9D9D9]
        shadow-[0px_1px_3px_rgba(0,0,0,0.10),0px_1px_2px_rgba(0,0,0,0.06)]
        p-4 sm:p-6 lg:p-8
      "
    >
      <h3 className="text-[16px] font-semibold text-gray-900 mb-3">
        Media Upload
      </h3>

      {/* DROP ZONE */}
      <label
        className={`
          w-full h-[160px] sm:h-[170px]
          border border-dashed border-[#D9D9D9]
          rounded-lg flex flex-col items-center justify-center
          text-sm cursor-pointer bg-white transition
          ${
            isUploading
              ? "opacity-60 cursor-not-allowed bg-gray-50"
              : "hover:bg-gray-50"
          }
        `}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          disabled={isUploading}
          onChange={(e) => onDropImages(e.target.files)}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex items-center">
            <FiLoader className="animate-spin text-[#00B8C8] mr-2" size={22} />
            <div className="text-gray-700 font-medium">Uploading...</div>
          </div>
        ) : (
          <>
            <div className="text-gray-800 font-medium text-center">
              Drop your images here or upload
            </div>
            <div className="text-xs text-gray-500 mt-1">JPG, PNG up to 5Mb</div>
          </>
        )}
      </label>

      {/* MAIN IMAGE */}
      {localImages.length > 0 && (
        <div className="mt-4 relative group">
          <img
            src={getImagePreview(localImages[0])}
            alt=""
            className="h-[200px] w-full object-contain object-center rounded-lg"
          />
          <button
            type="button"
            onClick={() => removeImage(0)}
            className="
              absolute top-2 right-2
              bg-black/60 text-white text-xs
              px-2 py-1 rounded
              opacity-0 group-hover:opacity-100 transition
            "
          >
            Remove
          </button>
        </div>
      )}

      {/* THUMBNAILS */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
        {localImages.slice(1).map((img, idx) => (
          <div key={idx} className="relative group">
            <img
              src={getImagePreview(img)}
              alt=""
              className="h-[96px] w-full object-contain object-center rounded-lg"
            />
            <button
              type="button"
              onClick={() => removeImage(idx + 1)}
              className="
                absolute top-1 right-1
                bg-black/60 text-white text-xs
                px-2 py-1 rounded
                opacity-0 group-hover:opacity-100 transition
              "
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {/* FOOTER */}
      <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 mt-8">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="
            h-11 px-8 rounded-lg border border-[#0F2F62]
            text-[#0F2F62] font-semibold text-sm
            hover:bg-[#0F2F62]/5 transition
          "
        >
          Back
        </button>

        <button
          type="button"
          onClick={syncAndNext}
          disabled={isSubmitting || isUploading}
          className="
            h-11 px-10 rounded-lg bg-[#00B8C8]
            text-white font-semibold text-sm
            hover:bg-[#009FB0] transition
            disabled:opacity-50
          "
        >
          Continue
        </button>
      </div>
    </div>
  );
}
